import { z } from "zod";

// ===== Enum Types =====
export const ProductType = z.enum([
  "POSTER", "BUSINESS_CARD", "LEAFLET", "STICKER",
  "HANGING_BANNER", "STAND_BANNER", "SOFT_COVER_BOOK", "HARD_COVER_BOOK", "OTHER",
]);
export type ProductType = z.infer<typeof ProductType>;

export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  POSTER: "포스터",
  BUSINESS_CARD: "명함",
  LEAFLET: "리플렛",
  STICKER: "스티커",
  HANGING_BANNER: "현수막",
  STAND_BANNER: "스탠드배너",
  SOFT_COVER_BOOK: "소프트커버 책자",
  HARD_COVER_BOOK: "하드커버 책자",
  OTHER: "기타",
};

export const MaterialType = z.enum(["SHEET_PAPER", "ROLL_PAPER", "INK", "INK_CONFIG", "OTHER"]);
export type MaterialType = z.infer<typeof MaterialType>;

export const MATERIAL_TYPE_LABELS: Record<string, string> = {
  SHEET_PAPER: "낱장 용지",
  ROLL_PAPER: "롤 용지",
  INK: "잉크",
  INK_CONFIG: "인쇄 구성",
  OTHER: "기타",
};

export const PaperType = z.enum(["SHEET_PAPER", "ROLL_PAPER"]);
export type PaperType = z.infer<typeof PaperType>;

export const PAPER_TYPE_LABELS: Record<string, string> = {
  SHEET_PAPER: "종이 규격 금액",
  ROLL_PAPER: "회배당 금액",
};

export const PaperUsageType = z.enum(["GENERAL", "COVER", "INNER", "END_PAPER"]);
export type PaperUsageType = z.infer<typeof PaperUsageType>;

export const PAPER_USAGE_TYPE_LABELS: Record<string, string> = {
  GENERAL: "일반",
  COVER: "표지",
  INNER: "내지",
};

export const OptionType = z.enum([
  "PAPER", "BINDING", "BOOK_COVER_PAPER", "BOOK_INNER_PAPER",
  "BOOK_INSERT_PAPER", "BOOK_END_PAPER", "PAPER_ORIENTATION", "PRINT",
]);
export type OptionType = z.infer<typeof OptionType>;

export const QuantityType = z.enum([
  "SINGLE", "SELECTABLE", "MULTIPLIED_BY_PRODUCT_QUANTITY", "SAME_AS_PRODUCT_QUANTITY",
]);
export type QuantityType = z.infer<typeof QuantityType>;

export const PreviewType = z.enum([
  "SELECT_BOX", "CHECK_BOX", "DROP_DOWN", "IMAGE_SELECT_BOX",
  "INPUT_NUMBER", "INPUT_STRING", "INPUT_DATE_TIME",
]);
export type PreviewType = z.infer<typeof PreviewType>;

export const TriggerType = z.enum(["AND", "OR", "NAND", "NOR"]);
export type TriggerType = z.infer<typeof TriggerType>;

export const TriggerVisibleType = z.enum(["HIDE", "DISABLE"]);
export type TriggerVisibleType = z.infer<typeof TriggerVisibleType>;

export const SystemAttributeType = z.enum([
  "BINDING_TYPE", "BINDING_DIRECTION", "PAPER_TYPE", "GRAMMAGE",
  "PRINT_COLOR_MODE", "PRINT_METHOD", "END_PAPER_PLACEMENT", "INSERT_PAGE_NUMBER",
  "PAPER_ORIENTATION",
]);
export type SystemAttributeType = z.infer<typeof SystemAttributeType>;

export const OptionAttributeValueType = z.enum([
  "BINDING_EVA", "BINDING_HOT_MELT", "BINDING_HOT_MELT_WING", "BINDING_PUR",
  "BINDING_SADDLE_STITCH", "BINDING_SPRING", "BINDING_CASE", "BINDING_RING",
  "BINDING_DIRECTION_LEFT", "BINDING_DIRECTION_RIGHT", "BINDING_DIRECTION_TOP",
  "END_PAPER_FRONT", "END_PAPER_BACK", "END_PAPER_FRONT_AND_BACK",
  "END_PAPER_FRONT_BOTH", "END_PAPER_BACK_BOTH", "END_PAPER_FRONT_AND_BACK_BOTH",
  "PRINT_METHOD_SINGLE_SIDED", "PRINT_METHOD_DOUBLE_SIDED",
  "PAPER_ORIENTATION_PORTRAIT", "PAPER_ORIENTATION_LANDSCAPE",
]);
export type OptionAttributeValueType = z.infer<typeof OptionAttributeValueType>;

export const BaseAreaUnit = z.enum(["SQUARE_M", "SQUARE_CM", "SQUARE_MM"]);
export type BaseAreaUnit = z.infer<typeof BaseAreaUnit>;

export const SheetSizePresetType = z.enum([
  "TYPE_A_FULL", "TYPE_A_CUT2", "TYPE_A_CUT4", "TYPE_A_CUT8", "TYPE_A_CUT16", "TYPE_A_CUT32",
  "TYPE_B_FULL", "TYPE_B_CUT2", "TYPE_B_CUT4", "TYPE_B_CUT8", "TYPE_B_CUT16", "TYPE_B_CUT32",
]);
export type SheetSizePresetType = z.infer<typeof SheetSizePresetType>;

// ===== SheetSizeSpec DTOs =====
// 일반(낱장) 용지 규격 마스터. roll_size_specs와 같은 구조이되 세로(height)와
// presetType(절수 분류)이 더 있다.
// - presetType != null: 전지/2절/4절... 절수 규격. MES의 절수 그리드가 이 값으로 칸을 찾고,
//   "전지 금액 기준 절수별 자동 계산"도 이 값으로 그룹핑한다. 회사당 절수별 1개.
// - presetType == null: 회사가 직접 만든 커스텀 규격(예: A0 841x1189). 개수 제한 없음.
/** 낱장 상품의 용지·인쇄 가격 기준: 기존(사이즈에 맞는 용지 1장에 1개) / 조판 */
export const PaperPricingBasisSchema = z.enum(["SIZE_FIT", "IMPOSITION"]);
export type PaperPricingBasis = z.infer<typeof PaperPricingBasisSchema>;

export const SheetSizeSpecDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  name: z.string(),
  width: z.number(),
  height: z.number(),
  presetType: SheetSizePresetType.nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type SheetSizeSpecDto = z.infer<typeof SheetSizeSpecDtoSchema>;

// ===== RollSizeSpec DTOs =====
// 백엔드에는 프리셋(enum) 개념이 없음. roll_size_specs는 전부 동등한 일반 row이며,
// A1/A2/B0 같은 값은 MaterialManagementPage.tsx의 ROLL_SIZE_QUICK_PRESETS에만 하드코딩되어
// "빠른 생성" 버튼으로 쓰이고, 클릭 시 일반 생성 API를 그대로 호출한다.
export const RollSizeSpecDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  name: z.string(),
  width: z.number(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type RollSizeSpecDto = z.infer<typeof RollSizeSpecDtoSchema>;

// ===== PaperSpec DTOs =====
export const PaperSpecDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  grammage: z.number(),
  thicknessMm: z.number(),
  isCoated: z.boolean(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type PaperSpecDto = z.infer<typeof PaperSpecDtoSchema>;

// ===== Category DTOs =====
export const ProductLightDtoSchema = z.object({
  id: z.number(),
  thumbnail: z.string().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  price: z.number(),
  displayOrder: z.number().optional(),
  isEnable: z.boolean().optional(), // 상품 활성화 여부 (판매 중/판매 중지)
  // 상품 상세 조회 시 포함되는 필드
  productType: ProductType.optional(),
  paperType: PaperType.optional(),
  categoryId: z.number().nullable().optional(),
  isSizeCustomizable: z.boolean().optional(),
  isInnerPaperCustomizable: z.boolean().optional(), // 내지 커스텀(구간 분할) 가능 여부 (책자 상품)
  isSingleSideEnabled: z.boolean().optional(), // 쇼핑몰에서 단면 인쇄 선택 허용
  isDoubleSideEnabled: z.boolean().optional(), // 쇼핑몰에서 양면 인쇄 선택 허용
  minWidth: z.number().nullable().optional(),
  maxWidth: z.number().nullable().optional(),
  minHeight: z.number().nullable().optional(),
  maxHeight: z.number().nullable().optional(),
  applyWasteRate: z.boolean().optional(), // 손지율 적용 여부
});
export type ProductLightDto = z.infer<typeof ProductLightDtoSchema>;

export const CategoryDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  name: z.string(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  isEnable: z.boolean(),
  displayOrder: z.number().optional(),
  products: z.array(ProductLightDtoSchema),
});
export type CategoryDto = z.infer<typeof CategoryDtoSchema>;

// ===== Material DTOs =====
export const MaterialDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  name: z.string(),
  type: MaterialType,
  paperSpecId: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type MaterialDto = z.infer<typeof MaterialDtoSchema>;

export const MaterialSheetPaperPriceTierDtoSchema = z.object({
  id: z.number(),
  materialSheetPaperId: z.number(),
  minQuantity: z.number(),
  price: z.number(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type MaterialSheetPaperPriceTierDto = z.infer<typeof MaterialSheetPaperPriceTierDtoSchema>;

export const MaterialSheetPaperDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  companyId: z.number(),
  paperColor: z.string().nullable().optional(),
  sheetSizeSpecId: z.number(),
  sheetSizeSpecName: z.string(),
  // presetType / width / height 는 sheetSizeSpec 에서 파생된 읽기 전용 값이다.
  // 쓰기(POST/PUT)에는 sheetSizeSpecId 만 보낸다.
  presetType: SheetSizePresetType.nullable().optional(),
  width: z.number(),
  height: z.number(),
  priceTiers: z.array(MaterialSheetPaperPriceTierDtoSchema),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type MaterialSheetPaperDto = z.infer<typeof MaterialSheetPaperDtoSchema>;

export const MaterialRollPaperDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  companyId: z.number(),
  paperColor: z.string().nullable().optional(),
  rollSizeSpecId: z.number(),
  rollSizeSpecName: z.string(),
  width: z.number(),
  // 가격 3필드는 함께 null(가격 미설정, 화면상 회색 '추가 전')이거나 함께 값이 있다.
  baseAreaSize: z.number().nullable().optional(),
  baseAreaUnit: BaseAreaUnit.nullable().optional(),
  price: z.number().nullable().optional(),
  hasPrice: z.boolean(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type MaterialRollPaperDto = z.infer<typeof MaterialRollPaperDtoSchema>;

export const InkDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  companyId: z.number(),
  inkColor: z.string().nullable().optional(),
  name: z.string(),
  isDeletable: z.boolean(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type InkDto = z.infer<typeof InkDtoSchema>;

export const InkConfigPriceTierDtoSchema = z.object({
  id: z.number(),
  materialInkConfigId: z.number(),
  minQuantity: z.number(),
  price: z.number(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type InkConfigPriceTierDto = z.infer<typeof InkConfigPriceTierDtoSchema>;

export const InkConfigDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  companyId: z.number(),
  inks: z.array(InkDtoSchema),
  name: z.string(),
  priceTiers: z.array(InkConfigPriceTierDtoSchema),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type InkConfigDto = z.infer<typeof InkConfigDtoSchema>;

export const OtherMaterialDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  companyId: z.number(),
  name: z.string(),
  price: z.number(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type OtherMaterialDto = z.infer<typeof OtherMaterialDtoSchema>;

// ===== Product DTOs =====
export const ProductConfigDtoSchema = z.object({
  productTypes: z.array(ProductType),
  paperTypes: z.array(PaperType),
});
export type ProductConfigDto = z.infer<typeof ProductConfigDtoSchema>;

export const ProductPaperDtoSchema = z.object({
  id: z.number(),
  productId: z.number(),
  materialId: z.number(),
  paperUsageType: PaperUsageType,
  minWidth: z.number().nullable().optional(),
  maxWidth: z.number().nullable().optional(),
  minHeight: z.number().nullable().optional(),
  maxHeight: z.number().nullable().optional(),
  isSizeCustomizable: z.boolean(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type ProductPaperDto = z.infer<typeof ProductPaperDtoSchema>;

/**
 * 상품에 연결된 용지를 Material(용지 재질) 이름으로 그룹핑한 응답 DTO
 */
export const GroupedProductPaperDtoSchema = z.object({
  materialName: z.string(),
  grammages: z.array(z.number()),
  paperIds: z.array(z.number()),
  paperUsageType: PaperUsageType,
  /** 그룹 중 쇼핑몰 용지 선택지에 노출되지 않는 용지 (판정은 백엔드 ShopPaperAvailabilityResolver) */
  shopUnavailablePapers: z
    .array(
      z.object({
        paperId: z.number(),
        materialName: z.string(),
        grammage: z.number().nullable().optional(),
        reason: z.enum([
          "NO_SHEET_SPEC",
          "NO_SHEET_PRICE",
          "NO_PRICED_ROLL_SPEC",
          "BOOK_COVER_NOT_SHEET",
          "END_PAPER_NOT_SHEET",
          "END_PAPER_NOT_HARD_COVER",
          "ROLL_PAPER_ON_SHEET_PRODUCT",
          "SHEET_PAPER_ON_ROLL_PRODUCT",
          "NOT_PAPER_MATERIAL",
        ]),
        message: z.string(),
      }),
    )
    .optional()
    .default([]),
});
export type GroupedProductPaperDto = z.infer<typeof GroupedProductPaperDtoSchema>;

/**
 * 동적 용지 옵션 필터링 응답 DTO
 *
 * 견적식 미리보기에서 용지 종류/평량 선택 시 상호 필터링을 위한 DTO
 * - 평량 먼저 선택 → 해당 평량을 지원하는 용지 종류만 반환
 * - 용지 종류 먼저 선택 → 해당 용지에서 사용 가능한 평량만 반환
 * - 둘 다 선택 시 → 매칭되는 Material ID 반환
 */
export const AvailablePaperOptionsDtoSchema = z.object({
  availableMaterialNames: z.array(z.string()),
  availableGrammages: z.array(z.number()),
  matchingMaterialId: z.number().nullable().optional(),
});
export type AvailablePaperOptionsDto = z.infer<typeof AvailablePaperOptionsDtoSchema>;

export const SizeTemplateDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  productId: z.number(),
  width: z.number(),
  height: z.number(),
  name: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  isSupported: z.boolean().nullable().optional(),
  supportReason: z.string().nullable().optional(),
});
export type SizeTemplateDto = z.infer<typeof SizeTemplateDtoSchema>;

// ===== ProductInkConfig DTOs =====
export const ProductInkConfigDtoSchema = z.object({
  id: z.number(),
  productId: z.number(),
  inkConfigId: z.number(),
  inkConfigName: z.string(),
  inkNames: z.array(z.string()),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type ProductInkConfigDto = z.infer<typeof ProductInkConfigDtoSchema>;

export const CreateProductInkConfigRequestSchema = z.object({
  inkConfigId: z.number(),
});
export type CreateProductInkConfigRequest = z.infer<typeof CreateProductInkConfigRequestSchema>;

// ===== Option DTOs =====
// 옵션 값 가격 결정 방식 (FIXED: 고정 단가, SPINE_TIER: 책등 두께 구간별 / null은 FIXED로 간주)
export const OptionPriceMode = z.enum(["FIXED", "SPINE_TIER"]);
export type OptionPriceMode = z.infer<typeof OptionPriceMode>;

// 책등 두께(세네카) 구간별 가격 (maxSpineThickness는 구간 상한, mm 단위)
export const SpinePriceTierDtoSchema = z.object({
  id: z.number(),
  maxSpineThickness: z.number(),
  price: z.number(),
});
export type SpinePriceTierDto = z.infer<typeof SpinePriceTierDtoSchema>;

// 수량 구간별 가격 (minQuantity는 구간 하한 수량)
// maxSpineThickness가 null이면 고정 단가 기준, 값이 있으면 해당 책등 두께 구간 기준의 수량 구간
export const QuantityPriceTierDtoSchema = z.object({
  id: z.number(),
  maxSpineThickness: z.number().nullable().optional(),
  minQuantity: z.number(),
  price: z.number(),
});
export type QuantityPriceTierDto = z.infer<typeof QuantityPriceTierDtoSchema>;

export const OptionAttributeValueDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  productId: z.number(),
  optionId: z.number(),
  optionAttributeId: z.number(),
  value: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  isDeletable: z.boolean(),
  isEnabled: z.boolean(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  type: OptionAttributeValueType.nullable().optional(),
  displayOrder: z.number(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  priceMode: OptionPriceMode.nullable().optional(),
  spinePriceTiers: z.array(SpinePriceTierDtoSchema).nullable().optional(),
  quantityPriceTiers: z.array(QuantityPriceTierDtoSchema).nullable().optional(),
});
export type OptionAttributeValueDto = z.infer<typeof OptionAttributeValueDtoSchema>;

export const OptionAttributeDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  productId: z.number(),
  optionId: z.number(),
  isDeletable: z.boolean(),
  isAddable: z.boolean(),
  fixedAttributeValueId: z.number().nullable().optional(),
  // 옵션 선택 화면에서 처음부터 선택돼 있는 속성값 ID.
  // 첫 속성값을 만들 때 서버가 자동으로 채우고, 기본값을 지우면 남은 값 중 첫 번째로 옮겨 간다.
  defaultAttributeValueId: z.number().nullable().optional(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  isMultiSelectable: z.boolean(),
  previewType: PreviewType.nullable().optional(),
  systemAttributeType: SystemAttributeType.nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  displayOrder: z.number(),
  isDeleted: z.boolean(),
  values: z.array(OptionAttributeValueDtoSchema),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type OptionAttributeDto = z.infer<typeof OptionAttributeDtoSchema>;

export const OptionDtoSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  productId: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isRequired: z.boolean(),
  isMultipleAllowed: z.boolean(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  quantityType: QuantityType,
  displayOrder: z.number().nullable().optional(),
  isDeletable: z.boolean(),
  optionType: OptionType.nullable().optional(),
  isDeleted: z.boolean(),
  attributes: z.array(OptionAttributeDtoSchema),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type OptionDto = z.infer<typeof OptionDtoSchema>;

// ===== Request Types =====
export const CreateCategoryRequestSchema = z.object({
  name: z.string(),
  isEnable: z.boolean().optional(),
});
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;

export const UpdateCategoryRequestSchema = z.object({
  name: z.string(),
  isEnable: z.boolean(),
});
export type UpdateCategoryRequest = z.infer<typeof UpdateCategoryRequestSchema>;

// ===== Display Order Request Types =====
export const CategoryOrderItemSchema = z.object({
  categoryId: z.number(),
  displayOrder: z.number(),
});
export type CategoryOrderItem = z.infer<typeof CategoryOrderItemSchema>;

export const BulkUpdateCategoryOrderRequestSchema = z.object({
  orders: z.array(CategoryOrderItemSchema),
});
export type BulkUpdateCategoryOrderRequest = z.infer<typeof BulkUpdateCategoryOrderRequestSchema>;

export const ProductOrderItemSchema = z.object({
  productId: z.number(),
  displayOrder: z.number(),
});
export type ProductOrderItem = z.infer<typeof ProductOrderItemSchema>;

export const BulkUpdateProductOrderRequestSchema = z.object({
  orders: z.array(ProductOrderItemSchema),
});
export type BulkUpdateProductOrderRequest = z.infer<typeof BulkUpdateProductOrderRequestSchema>;

export const CreateProductRequestSchema = z.object({
  categoryId: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
  productType: ProductType,
  paperType: PaperType,
  isSizeCustomizable: z.boolean().optional(),
  isInnerPaperCustomizable: z.boolean().optional(), // 내지 커스텀(구간 분할) 가능 여부 (책자 상품)
  isSingleSideEnabled: z.boolean().optional(), // 쇼핑몰에서 단면 인쇄 선택 허용
  isDoubleSideEnabled: z.boolean().optional(), // 쇼핑몰에서 양면 인쇄 선택 허용
  minWidth: z.number().nullable().optional(),
  minHeight: z.number().nullable().optional(),
  maxWidth: z.number().nullable().optional(),
  maxHeight: z.number().nullable().optional(),
  isQuoteOnly: z.boolean().optional(), // 견적 문의 전용 상품
});
export type CreateProductRequest = z.infer<typeof CreateProductRequestSchema>;

export const UpdateProductRequestSchema = z.object({
  categoryId: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
  isBestSeller: z.boolean().nullable().optional(),
  isEnable: z.boolean().nullable().optional(), // 상품 활성화 여부 (판매 중/판매 중지)
  isSizeCustomizable: z.boolean().nullable().optional(),
  isInnerPaperCustomizable: z.boolean().nullable().optional(), // 내지 커스텀(구간 분할) 가능 여부 (책자 상품)
  isSingleSideEnabled: z.boolean().nullable().optional(), // 쇼핑몰에서 단면 인쇄 선택 허용
  isDoubleSideEnabled: z.boolean().nullable().optional(), // 쇼핑몰에서 양면 인쇄 선택 허용
  minWidth: z.number().nullable().optional(),
  minHeight: z.number().nullable().optional(),
  maxWidth: z.number().nullable().optional(),
  maxHeight: z.number().nullable().optional(),
  applyWasteRate: z.boolean().nullable().optional(), // 손지율 적용 여부
  isQuoteOnly: z.boolean().nullable().optional(), // 견적 문의 전용 상품
  paperPricingBasis: PaperPricingBasisSchema.nullable().optional(), // 낱장 용지·인쇄 가격 기준
  impositionSheetSizeSpecId: z.number().nullable().optional(), // 조판 기준 용지 규격
  impositionGapMm: z.number().nullable().optional(), // 조판 작업물 간격(mm)
});
export type UpdateProductRequest = z.infer<typeof UpdateProductRequestSchema>;

export const DuplicateProductRequestSchema = z.object({
  name: z.string(),
  targetCategoryId: z.number().nullable().optional(),
});
export type DuplicateProductRequest = z.infer<typeof DuplicateProductRequestSchema>;

export const CreateProductPaperRequestSchema = z.object({
  materialId: z.number(),
  paperUsageType: PaperUsageType.optional(),
  minWidth: z.number().nullable().optional(),
  maxWidth: z.number().nullable().optional(),
  minHeight: z.number().nullable().optional(),
  maxHeight: z.number().nullable().optional(),
  isSizeCustomizable: z.boolean().optional(),
});
export type CreateProductPaperRequest = z.infer<typeof CreateProductPaperRequestSchema>;

export const CreateSizeTemplateRequestSchema = z.object({
  width: z.number(),
  height: z.number(),
  name: z.string().nullable().optional(),
});
export type CreateSizeTemplateRequest = z.infer<typeof CreateSizeTemplateRequestSchema>;

export const UpdateSizeTemplateRequestSchema = z.object({
  width: z.number(),
  height: z.number(),
  name: z.string().nullable().optional(),
});
export type UpdateSizeTemplateRequest = z.infer<typeof UpdateSizeTemplateRequestSchema>;

export const CreateOptionRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  isMultipleAllowed: z.boolean().optional(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  quantityType: QuantityType.optional(),
  displayOrder: z.number().nullable().optional(),
  optionType: OptionType.nullable().optional(),
});
export type CreateOptionRequest = z.infer<typeof CreateOptionRequestSchema>;

export const CreateOptionAttributeRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  isAddable: z.boolean().optional(),
  fixedAttributeValueId: z.number().nullable().optional(),
  defaultAttributeValueId: z.number().nullable().optional(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  isMultiSelectable: z.boolean().optional(),
  previewType: PreviewType.nullable().optional(),
  systemAttributeType: SystemAttributeType.nullable().optional(),
});
export type CreateOptionAttributeRequest = z.infer<typeof CreateOptionAttributeRequestSchema>;

export const SpinePriceTierRequestSchema = z.object({
  maxSpineThickness: z.number(),
  price: z.number(),
});
export type SpinePriceTierRequest = z.infer<typeof SpinePriceTierRequestSchema>;

// 수량 구간별 가격 요청
// maxSpineThickness를 지정하면 같은 요청의 spinePriceTiers 중 해당 상한 구간에 매달린 수량 구간이 된다
export const QuantityPriceTierRequestSchema = z.object({
  maxSpineThickness: z.number().nullable().optional(),
  minQuantity: z.number(),
  price: z.number(),
});
export type QuantityPriceTierRequest = z.infer<typeof QuantityPriceTierRequestSchema>;

export const CreateOptionAttributeValueRequestSchema = z.object({
  value: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  isEnabled: z.boolean().optional(),
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerType.nullable().optional(),
  triggerVisibleType: TriggerVisibleType.nullable().optional(),
  type: OptionAttributeValueType.nullable().optional(),
  materialIds: z.array(z.number()).nullable().optional(),
  // 가격 결정 방식 (null이면 변경 없음)
  priceMode: OptionPriceMode.nullable().optional(),
  // null이면 변경 없음, 빈 배열이면 전체 삭제
  spinePriceTiers: z.array(SpinePriceTierRequestSchema).nullable().optional(),
  // null이면 변경 없음, 빈 배열이면 전체 삭제
  quantityPriceTiers: z.array(QuantityPriceTierRequestSchema).nullable().optional(),
});
export type CreateOptionAttributeValueRequest = z.infer<typeof CreateOptionAttributeValueRequestSchema>;

// ===== Price Calculation =====
/**
 * 내지 구간 - 내지를 순서대로 나눈 한 구간.
 * 구간마다 용지(종류 + 평량 = materialId)와 페이지 수를 갖고, 총 페이지 수는 구간 합계다.
 */
export const InnerPaperSectionSchema = z.object({
  materialId: z.number(),
  pageCount: z.number(),
});
export type InnerPaperSection = z.infer<typeof InnerPaperSectionSchema>;

export const PriceCalculationRequestSchema = z.object({
  width: z.number(),
  height: z.number(),
  quantity: z.number(),
  materialId: z.number().nullable().optional(),
  inkConfigId: z.number().nullable().optional(),
  options: z.record(z.string(), z.number()).optional(),
  isDoubleSided: z.boolean().optional(),  // 양면 인쇄 여부 (단일 용지 상품용)
  // 책자 상품용 필드
  pageCount: z.number().nullable().optional(),
  coverMaterialId: z.number().nullable().optional(),
  innerMaterialId: z.number().nullable().optional(),
  endPaperMaterialId: z.number().nullable().optional(),
  insertCount: z.number().nullable().optional(),
  // 내지 구간 목록 (내지 커스텀이 켜진 책자 상품에서만 사용)
  innerPaperSections: z.array(InnerPaperSectionSchema).nullable().optional(),
  // 책자 상품용 인쇄 설정 (표지/내지 분리)
  coverInkConfigId: z.number().nullable().optional(),    // 표지 잉크 설정 ID
  coverIsDoubleSided: z.boolean().optional(),            // 표지 양면 여부 (기본 true)
  innerInkConfigId: z.number().nullable().optional(),    // 내지 잉크 설정 ID
  innerIsDoubleSided: z.boolean().optional(),            // 내지 양면 여부 (기본 true)
});
export type PriceCalculationRequest = z.infer<typeof PriceCalculationRequestSchema>;

export const OptionPriceDetailSchema = z.object({
  optionId: z.number(),
  optionName: z.string(),
  valueId: z.number(),
  valueName: z.string(),
  price: z.number(),
});
export type OptionPriceDetail = z.infer<typeof OptionPriceDetailSchema>;

export const PriceBreakdownSchema = z.object({
  paperUnitPrice: z.number(),
  inkUnitPrice: z.number(),
  optionDetails: z.array(OptionPriceDetailSchema),
  quantity: z.number(),
});
export type PriceBreakdown = z.infer<typeof PriceBreakdownSchema>;

/**
 * 손지율 적용 정보 (단일 용지 상품용)
 */
export const WasteRateInfoSchema = z.object({
  applied: z.boolean(),                    // 손지율 적용 여부
  wasteRatePercent: z.number(),            // 적용된 손지율 (%)
  originalQuantity: z.number(),            // 원래 수량
  wasteQuantity: z.number(),               // 손지량
  effectiveQuantity: z.number(),           // 최종 용지 수량 (원래 + 손지)
  originalPrice: z.number(),               // 원래 용지 가격
  wastePrice: z.number(),                  // 손지 금액
  inkCount: z.number(),                    // 인쇄 색상 수 (1~4도)
  isDoubleSided: z.boolean(),              // 양면 인쇄 여부
});
export type WasteRateInfo = z.infer<typeof WasteRateInfoSchema>;

/**
 * 개별 용지의 손지 정보 (표지, 내지, 면지 등)
 */
export const PaperWasteInfoSchema = z.object({
  paperType: z.string(),                   // "cover", "inner", "endPaper"
  wasteRatePercent: z.number(),            // 적용된 손지율 (%)
  originalSheetCount: z.number(),          // 원래 장수
  wasteSheetCount: z.number(),             // 손지 장수
  unitPrice: z.number(),                   // 용지 단가 (장당)
  originalPrice: z.number(),               // 원래 용지 가격
  wastePrice: z.number(),                  // 손지 금액
  inkCount: z.number(),                    // 인쇄 색상 수 (1~4도)
  isDoubleSided: z.boolean(),              // 양면 인쇄 여부
});
export type PaperWasteInfo = z.infer<typeof PaperWasteInfoSchema>;

/**
 * 책자 상품용 손지율 정보 (표지/내지 분리)
 */
export const BookWasteRateInfoSchema = z.object({
  applied: z.boolean(),                    // 손지율 적용 여부
  cover: PaperWasteInfoSchema.nullable().optional(),   // 표지 손지 정보
  inner: PaperWasteInfoSchema.nullable().optional(),   // 내지 손지 정보
  endPaper: PaperWasteInfoSchema.nullable().optional(), // 면지 손지 정보 (양장본)
  totalOriginalPrice: z.number(),          // 총 원래 용지 가격
  totalWastePrice: z.number(),             // 총 손지 금액
});
export type BookWasteRateInfo = z.infer<typeof BookWasteRateInfoSchema>;

export const PriceCalculationResultSchema = z.object({
  paperPrice: z.number(),                  // 총 용지 가격 (손지 포함)
  inkPrice: z.number(),
  optionPrice: z.number(),
  subtotal: z.number(),
  vat: z.number(),
  total: z.number(),
  selectedMaterialId: z.number().nullable().optional(),
  selectedSheetPaperId: z.number().nullable().optional(),
  selectedRollPaperId: z.number().nullable().optional(),
  breakdown: PriceBreakdownSchema,
  // 손지율 정보 (단일 용지 상품용)
  wasteRateInfo: WasteRateInfoSchema.nullable().optional(),
  // 책자 상품용 손지율 정보 (표지/내지 분리)
  bookWasteRateInfo: BookWasteRateInfoSchema.nullable().optional(),
  // 책자 상품용 필드
  spineThickness: z.number().nullable().optional(),
  coverPaperPrice: z.number().nullable().optional(),   // 표지 용지 가격 (손지 미포함)
  innerPaperPrice: z.number().nullable().optional(),   // 내지 용지 가격 (손지 미포함)
  coverWastePrice: z.number().nullable().optional(),   // 표지 손지 금액
  innerWastePrice: z.number().nullable().optional(),   // 내지 손지 금액
});
export type PriceCalculationResult = z.infer<typeof PriceCalculationResultSchema>;

// ===== Utility constants =====
export const BOOK_PRODUCT_TYPES = ["SOFT_COVER_BOOK", "HARD_COVER_BOOK"] as const;
export const SHEET_PAPER_PRODUCT_TYPES = ["POSTER", "BUSINESS_CARD", "LEAFLET", "STICKER", "OTHER"] as const;
export const ROLL_PAPER_PRODUCT_TYPES = ["HANGING_BANNER", "STAND_BANNER"] as const;

export function isBookProductType(type: string): boolean {
  return BOOK_PRODUCT_TYPES.includes(type as any);
}

export function isRollPaperProductType(type: string): boolean {
  return ROLL_PAPER_PRODUCT_TYPES.includes(type as any);
}

// ===== Inventory Types =====
export const InventoryChangeType = z.enum(["RECEIVE", "DEDUCT", "RETURN"]);
export type InventoryChangeType = z.infer<typeof InventoryChangeType>;

export const INVENTORY_CHANGE_TYPE_LABELS: Record<string, string> = {
  RECEIVE: "입고",
  DEDUCT: "차감",
  RETURN: "반품",
};

export const MaterialInventoryDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  materialName: z.string().optional().nullable(),
  materialType: z.string().optional().nullable(),
  quantity: z.number(),
  updatedAt: z.string(),
});
export type MaterialInventoryDto = z.infer<typeof MaterialInventoryDtoSchema>;

export const MaterialInventoryLogDtoSchema = z.object({
  id: z.number(),
  materialId: z.number(),
  changeType: InventoryChangeType,
  previousQuantity: z.number(),
  changeQuantity: z.number(),
  currentQuantity: z.number(),
  memo: z.string().nullable().optional(),
  createdAt: z.string(),
  createdBy: z.string().nullable().optional(),
});
export type MaterialInventoryLogDto = z.infer<typeof MaterialInventoryLogDtoSchema>;

export const InventoryRequestSchema = z.object({
  quantity: z.number(),
  memo: z.string().optional(),
});
export type InventoryRequest = z.infer<typeof InventoryRequestSchema>;

export const InventoryLogPageResponseSchema = z.object({
  content: z.array(MaterialInventoryLogDtoSchema),
  totalElements: z.number(),
  totalPages: z.number(),
  number: z.number(),
  size: z.number(),
});
export type InventoryLogPageResponse = z.infer<typeof InventoryLogPageResponseSchema>;

// ===== Estimate Preview Types =====

/**
 * 견적식 미리보기 - 사이즈 정보
 */
export const SizeInfoSchema = z.object({
  sizeTemplateId: z.number(),
  width: z.number(),
  height: z.number(),
  displayName: z.string(), // "90x50mm"
});
export type SizeInfo = z.infer<typeof SizeInfoSchema>;

/**
 * 견적식 미리보기 - 용지 옵션 정보
 */
export const PaperOptionInfoSchema = z.object({
  materialId: z.number(),
  materialName: z.string(), // "아트지"
  grammage: z.number(), // 180
  displayName: z.string(), // "아트지 180g"
});
export type PaperOptionInfo = z.infer<typeof PaperOptionInfoSchema>;

/**
 * 견적식 미리보기 - 셀 데이터
 */
export const EstimateCellSchema = z.object({
  sizeTemplateId: z.number(),
  materialId: z.number(),
  sheetSpecName: z.string().nullable(), // "국전지 16절" or null
  unitPrice: z.number().nullable(),
  totalPrice: z.number().nullable(),
  isSupported: z.boolean(),
  reason: z.string().nullable(),
  impositionCount: z.number().nullable().optional(), // 조판 기준: 기준 용지 한 장에 앉는 개수
  sheetCount: z.number().nullable().optional(), // 조판 기준: 필요 장수
});
export type EstimateCell = z.infer<typeof EstimateCellSchema>;

/**
 * 견적식 미리보기 응답
 *
 * 상품의 사이즈 템플릿과 용지 조합에 대해
 * 각 조합별 절지 규격과 가격을 표 형태로 제공합니다.
 */
export const EstimatePreviewResponseSchema = z.object({
  sizeTemplates: z.array(SizeInfoSchema),
  paperOptions: z.array(PaperOptionInfoSchema),
  cells: z.array(EstimateCellSchema),
});
export type EstimatePreviewResponse = z.infer<typeof EstimatePreviewResponseSchema>;

/**
 * 견적식 미리보기 요청
 */
export const EstimatePreviewRequestSchema = z.object({
  quantity: z.number().optional().default(1),
});
export type EstimatePreviewRequest = z.infer<typeof EstimatePreviewRequestSchema>;

// ===== 상품 삭제 사용처 확인 =====

/**
 * 상품을 아직 쓰고 있는 곳 한 건.
 * 백엔드 `ProductInUseException.Usage` 와 1:1 대응한다.
 *
 * - `type`: "ACTIVE_ORDER" | "CART" | "QUOTE_INQUIRY" | "EVENT_DISCOUNT".
 *   백엔드가 나중에 종류를 늘려도 화면이 깨지지 않도록 문자열로 받는다.
 * - `label`: 그대로 화면에 노출할 한글 이름 (예: "진행 중인 주문")
 */
export const ProductUsageSchema = z.object({
  type: z.string(),
  label: z.string(),
  count: z.number(),
});
export type ProductUsage = z.infer<typeof ProductUsageSchema>;

/**
 * 상품 삭제 시도 결과.
 *
 * 사용 중인 상품을 `confirmed` 없이 지우려 하면 백엔드가 409 로 막고 사용처를 알려준다.
 * 그 경우를 예외가 아니라 `deleted: false` 인 정상 결과로 돌려준다 —
 * 삭제가 "실패" 한 게 아니라 사용자 확인을 기다리는 상태이기 때문이다.
 */
export interface DeleteProductResult {
  /** 실제로 삭제됐는지 */
  deleted: boolean;
  /** `deleted: false` 일 때 이 상품을 쓰고 있는 곳의 요약. 삭제된 경우 빈 배열 */
  usages: ProductUsage[];
  /** 백엔드가 보낸 안내 문구 (`deleted: false` 일 때만) */
  message?: string;
}

